package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.layout.engine.LayoutMode
import kotlin.jvm.JvmName
import kotlin.jvm.JvmStatic
import kotlin.math.ceil

/**
 * 可折叠虚拟树：把树结构按当前展开状态扁平化为可见行列表，再按等高列表窗口化渲染。
 *
 * 每个 [TreeNode] 可为 leaf 或 branch；branch 在 [expandedIds] 中时其子节点插入到可见列表。
 * [flattenedItems] 返回当前可见行（含每行 depth），`itemCount = flattenedItems().size`，
 * 窗口计算与 [VirtualList] 一致。
 *
 * [toggleExpand] 切换某 branch 展开状态并触发窗口重算。窗口子节点 0 基排布，
 * 故覆盖 [contentOffsetY] 叠加窗口首项偏移。
 */
@Suppress("TooManyFunctions", "MagicNumber")
class VirtualTree private constructor() : ScrollableContainer<VirtualTree>() {

    /** 树节点（不可变）。 */
    class TreeNode internal constructor(
        @get:JvmName("id") val id: String,
        val isLeaf: Boolean,
        @get:JvmName("data") val data: Any?,
        @get:JvmName("children") val children: List<TreeNode>,
    ) {
        companion object {
            /** 叶子节点便捷构造。 */
            @JvmStatic
            fun leaf(id: String, data: Any?): TreeNode = TreeNode(id, true, data, emptyList())

            /** 分支节点便捷构造。 */
            @JvmStatic
            fun branch(id: String, data: Any?, children: List<TreeNode>?): TreeNode =
                TreeNode(id, false, data, children?.toList() ?: emptyList())
        }
    }

    /** 扁平化后的一行：节点 + 其在树中的深度。 */
    data class FlatRow(@get:JvmName("node") val node: TreeNode, @get:JvmName("depth") val depth: Int)

    private var rootsField: List<TreeNode> = emptyList()
    private val expandedIds = LinkedHashSet<String>()
    private var itemHeightField: Float = 18f
    private var indentPerDepthField: Float = 12f
    private var bufferField: Int = 2

    /** 缓存的扁平可见行，roots/expandedIds 变化时重算。 */
    private var flattened: List<FlatRow> = emptyList()

    private var windowChildren: List<UIElement<*>> = emptyList()
    private var lastFirst = -1
    private var lastLast = -1
    private var lastCount = -1
    private var materializer: WindowMaterializer? = null

    init {
        styleField.layout().mode = LayoutMode.COLUMN
    }

    override fun self(): VirtualTree = this

    // -------- 属性 --------

    fun roots(roots: List<TreeNode>?): VirtualTree = apply {
        rootsField = roots?.toList() ?: emptyList()
        rebuildFlattened()
        scrollY(scrollY())
        invalidateWindow()
    }

    fun roots(): List<TreeNode> = rootsField

    fun itemHeight(h: Float): VirtualTree = apply {
        itemHeightField = h.coerceAtLeast(1f)
        invalidateWindow()
    }

    fun itemHeight(): Float = itemHeightField

    fun indentPerDepth(px: Float): VirtualTree = apply { indentPerDepthField = px.coerceAtLeast(0f) }

    fun indentPerDepth(): Float = indentPerDepthField

    fun buffer(b: Int): VirtualTree = apply {
        bufferField = b.coerceAtLeast(0)
        invalidateWindow()
    }

    fun buffer(): Int = bufferField

    // -------- 展开 / 折叠 --------

    fun isExpanded(id: String): Boolean = expandedIds.contains(id)

    /** 切换某 branch 的展开状态。 */
    fun toggleExpand(id: String?): VirtualTree = apply {
        if (id != null) {
            if (!expandedIds.remove(id)) {
                expandedIds.add(id)
            }
            rebuildFlattened()
            scrollY(scrollY())
            invalidateWindow()
        }
    }

    fun expand(id: String?): VirtualTree = apply {
        if (id != null && expandedIds.add(id)) {
            rebuildFlattened()
            invalidateWindow()
        }
    }

    fun collapse(id: String?): VirtualTree = apply {
        if (id != null && expandedIds.remove(id)) {
            rebuildFlattened()
            scrollY(scrollY())
            invalidateWindow()
        }
    }

    // -------- 扁平化 --------

    /** 按当前展开状态把树扁平化为可见行列表。 */
    private fun rebuildFlattened() {
        val result = ArrayList<FlatRow>()
        appendVisible(rootsField, 0, result)
        flattened = result
    }

    private fun appendVisible(nodes: List<TreeNode>, depth: Int, out: MutableList<FlatRow>) {
        for (node in nodes) {
            out.add(FlatRow(node, depth))
            if (!node.isLeaf && expandedIds.contains(node.id)) {
                appendVisible(node.children, depth + 1, out)
            }
        }
    }

    fun flattenedItems(): List<FlatRow> = flattened

    fun itemCount(): Int = flattened.size

    // -------- 内容尺寸 / 偏移 --------

    override fun requiresContentMeasure(): Boolean = false

    override fun contentHeight(): Float = flattened.size * itemHeightField

    override fun contentOffsetY(): Float = firstVisible() * itemHeightField - scrollY()

    // -------- 窗口范围 --------

    fun firstVisible(): Int = ((scrollY() / itemHeightField).toInt() - bufferField).coerceAtLeast(0)

    fun lastVisible(): Int {
        val visibleCount = ceil(viewportHeight() / itemHeightField).toInt()
        val raw = (scrollY() / itemHeightField).toInt() + visibleCount + bufferField
        return raw.coerceAtMost(flattened.size - 1)
    }

    // -------- 子节点管理 --------

    override fun children(): List<UIElement<*>> = windowChildren

    fun setWindowChildren(children: List<UIElement<*>>?) {
        windowChildren = children ?: emptyList()
        // FR-30 B1：windowChildren 经 children() 返回、绕过会标脏的 child()/replaceChildren()，
        // 树展开/折叠/滚动改变可见行时须显式标布局脏，否则整树布局 memo 命中返回过期布局、新窗口项不被定位（内容消失 / 堆顶）。
        markLayoutDirty()
    }

    fun materializeWindow(m: WindowMaterializer) {
        materializer = m
    }

    override fun onScrollStateChanged() {
        super.onScrollStateChanged()
        invalidateWindow()
    }

    private fun invalidateWindow() {
        val m = materializer ?: return
        val count = flattened.size
        if (count <= 0) {
            if (windowChildren.isNotEmpty()) {
                lastFirst = -1
                lastLast = -1
                lastCount = 0
                m.onWindowChanged(this, 0, -1)
            }
            return
        }
        val first = firstVisible()
        val last = lastVisible()
        if (first == lastFirst && last == lastLast && count == lastCount) {
            return
        }
        lastFirst = first
        lastLast = last
        lastCount = count
        m.onWindowChanged(this, first, last)
    }

    fun interface WindowMaterializer {
        /**
         * 窗口范围变化时调用。
         *
         * @param tree  VirtualTree 实例
         * @param first 第一个需实例化的可见行 index（含）
         * @param last  最后一个需实例化的可见行 index（含）；无可见行时 last < first
         */
        fun onWindowChanged(tree: VirtualTree, first: Int, last: Int)
    }

    companion object {
        @JvmStatic
        fun create(): VirtualTree = VirtualTree()
    }
}
